/**
 * Case document upload policy (must match backend `DocumentParser.ALLOWED_EXTENSIONS` and size cap).
 * Types: pdf, doc, jpg, tif, gif; max 6 MB; filename character rules.
 */

const MB = 1024 * 1024;
export const DOCUMENT_UPLOAD_MAX_BYTES = 6 * MB;

/** HTML `accept` for file inputs (includes common aliases). */
export const DOCUMENT_FILE_ACCEPT = ".pdf,.doc,.jpg,.jpeg,.tif,.tiff,.gif,application/pdf,application/msword,image/jpeg,image/gif,image/tiff";

const ALLOWED_EXT = new Set(["pdf", "doc", "jpg", "jpeg", "gif", "tif", "tiff"]);

// a-z A-Z Spanish letters ñ, digits, _ - . space ( )
const FILENAME_OK = /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ _\-.\(\)]+$/u;

function extensionOf(name: string): string {
  const i = name.lastIndexOf(".");
  if (i < 0) return "";
  return name.slice(i + 1).toLowerCase();
}

export type ClientDocumentCheckResult = { ok: true } | { ok: false; message: string };

/**
 * Client-side checks before multipart upload (mirrors server rules).
 */
export function validateClientDocumentFile(file: File): ClientDocumentCheckResult {
  if (!file || !(file instanceof File)) {
    return { ok: false, message: "No file selected." };
  }
  const name = (file.name || "").trim();
  if (!name) {
    return { ok: false, message: "No file selected." };
  }
  const base = name.replace(/^.*[/\\]/, "");
  if (!FILENAME_OK.test(base)) {
    return {
      ok: false,
      message:
        "Invalid file name. Use only letters (including á é í ó ú ñ), numbers, spaces, _ - . ( )",
    };
  }
  const ext = extensionOf(base);
  if (!ext || !ALLOWED_EXT.has(ext)) {
    return {
      ok: false,
      message: "File type not allowed. Use PDF, DOC, JPG, TIF, or GIF.",
    };
  }
  if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
    return {
      ok: false,
      message: `Each file must be at most ${DOCUMENT_UPLOAD_MAX_BYTES / MB} MB.`,
    };
  }
  return { ok: true };
}
